import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SEO from '@/components/SEO';
import { API_URL } from '@/lib/utils';
import { 
  Shield, Award, FlaskConical, GraduationCap, Laptop, Building, 
  FileCheck, Presentation, ChevronRight, ArrowRight
} from 'lucide-react';

const iconMap = {
  Shield, Award, FlaskConical, GraduationCap, Laptop, Building, FileCheck, Presentation
};

const categories = ['All', 'Accreditation', 'Training', 'Technology', 'Consulting'];

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API_URL}/services?active_only=true`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const filteredServices = activeCategory === 'All' 
    ? services 
    : services.filter(s => s.category === activeCategory);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="services-page">
      <SEO 
        title="Healthcare Services - NABH, NABL, Training & Digital Solutions"
        description="Comprehensive healthcare services: NABH accreditation, NABL certification, healthcare training, digital healthcare solutions, hospital planning, TPA empanelment, and CME workshops."
        keywords="NABH accreditation services, NABL certification, healthcare training India, hospital consulting, TPA empanelment, healthcare courses"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <span className="text-[#2eb24c] font-semibold text-sm uppercase tracking-wider">Our Services</span>
          <h1 className="font-manrope text-4xl md:text-5xl font-bold text-[#1a2a5e] mt-2 mb-6">
            Comprehensive Healthcare Solutions
          </h1>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            From accreditation consulting to digital transformation, we provide end-to-end solutions 
            to help healthcare facilities achieve excellence in quality and patient care.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map((category) => (
              <Button
                key={category}
                variant={activeCategory === category ? 'default' : 'outline'}
                className={activeCategory === category 
                  ? 'bg-[#1a2a5e] hover:bg-[#2d4a8e]' 
                  : 'border-gray-300 hover:border-[#1a2a5e] hover:text-[#1a2a5e]'
                }
                onClick={() => setActiveCategory(category)}
                data-testid={`filter-${category.toLowerCase()}`}
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service) => {
              const IconComponent = iconMap[service.icon] || Shield;
              return (
                <Link 
                  key={service.id} 
                  to={`/services/${service.slug}`}
                  className="group"
                  data-testid={`service-${service.slug}`}
                >
                  <Card className="h-full border border-gray-100 hover:border-[#1a2a5e]/20 hover:shadow-xl transition-all duration-300 service-card overflow-hidden">
                    <CardContent className="p-0">
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="service-icon text-[#1a2a5e] group-hover:text-white">
                            <IconComponent className="h-7 w-7" />
                          </div>
                          <span className="text-xs font-medium text-[#2eb24c] bg-[#2eb24c]/10 px-3 py-1 rounded-full">
                            {service.category}
                          </span>
                        </div>
                        <h3 className="font-manrope font-bold text-xl text-[#1a2a5e] mb-3 group-hover:text-[#2eb24c] transition-colors">
                          {service.title}
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed mb-4">
                          {service.short_description}
                        </p>
                        {service.features && service.features.length > 0 && (
                          <ul className="space-y-2 mb-4">
                            {service.features.slice(0, 3).map((feature, i) => (
                              <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                                <div className="w-1.5 h-1.5 bg-[#2eb24c] rounded-full" />
                                {feature}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                        <span className="inline-flex items-center text-[#1a2a5e] font-semibold text-sm group-hover:text-[#2eb24c]">
                          Learn More <ChevronRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>

          {filteredServices.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No services found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1a2a5e] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-manrope text-3xl font-bold mb-4">
            Need Help Choosing the Right Service?
          </h2>
          <p className="text-blue-100 mb-8">
            Our experts will analyze your requirements and recommend the best solutions for your healthcare facility.
          </p>
          <Button asChild size="lg" className="bg-[#2eb24c] hover:bg-[#259a40]">
            <Link to="/contact">
              Get Free Consultation <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Services;
